import type { FieldError, Problem } from "./contract.ts";

/**
 * `contracts/client-api.md` § Problems to surface: every non-2xx response is parsed as an
 * RFC 9457 problem, and a response that is not valid problem JSON is wrapped into a
 * synthetic problem of type `transport` carrying the status and the request id, so
 * downstream code handles one shape.
 */

/** The `type` of a synthetic problem for a response that carried no problem body, or for a
 * network failure and a timeout, which carry status 0. */
export const TRANSPORT_PROBLEM = "transport";

/** The 503 001 answers when the instance had no database connection free for the request within
 * its acquire timeout (2026-09-29): busy rather than down, with `Retry-After`. */
export const OVERLOADED = "overloaded";

/** The error every failed request throws: the problem, and the headers a handler needs. */
export class ApiError extends Error {
  readonly problem: Problem;
  readonly status: number;
  readonly requestId: string;
  readonly retryAfterMs: number | null;
  readonly headers: Headers;

  constructor(problem: Problem, headers: Headers = new Headers()) {
    super(problem.title);
    this.name = "ApiError";
    this.problem = problem;
    this.status = problem.status;
    this.requestId = problem.request_id;
    this.headers = headers;
    this.retryAfterMs = retryAfterMs(headers);
  }

  /** True for a network failure or a timeout, which never reached a server answer. */
  get isTransport(): boolean {
    return this.problem.type === TRANSPORT_PROBLEM && this.status === 0;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** `Retry-After` in milliseconds, whether it is seconds or an HTTP date; null when absent. */
export function retryAfterMs(headers: Headers, now: number = Date.now()): number | null {
  const raw = headers.get("Retry-After");
  if (raw === null) return null;
  const seconds = Number(raw);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const at = Date.parse(raw);
  return Number.isNaN(at) ? null : Math.max(0, at - now);
}

/** The `type` suffix after `/problems/`, which is how the contract's table names types. */
export function problemTypeOf(type: unknown): string {
  if (typeof type !== "string" || type.length === 0) return TRANSPORT_PROBLEM;
  const index = type.lastIndexOf("/problems/");
  return index >= 0 ? type.slice(index + "/problems/".length) : type;
}

function isProblemBody(value: unknown): value is Record<string, unknown> & { title: string; status: number } {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { title?: unknown }).title === "string" &&
    typeof (value as { status?: unknown }).status === "number"
  );
}

/** Parse a non-2xx response into a problem. Never throws: an unparseable body becomes the
 * synthetic `transport` problem with the response status. */
export async function parseProblem(response: Response, requestId: string): Promise<Problem> {
  const headerId = response.headers.get("X-Request-Id") ?? requestId;
  const contentType = response.headers.get("Content-Type") ?? "";
  if (/json/i.test(contentType)) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    if (isProblemBody(body)) {
      const { type, title, status, detail, instance, request_id, errors, conflict, required_role, ...rest } = body;
      const extensions = Object.fromEntries(Object.entries(rest).filter(([name]) => name !== "errors"));
      return {
        type: problemTypeOf(type),
        title,
        status,
        ...(typeof detail === "string" ? { detail } : {}),
        ...(typeof instance === "string" ? { instance } : {}),
        request_id: typeof request_id === "string" ? request_id : headerId,
        ...(Array.isArray(errors) ? { errors: errors as readonly FieldError[] } : {}),
        ...(typeof conflict === "object" && conflict !== null ? { conflict: conflict as NonNullable<Problem["conflict"]> } : {}),
        ...(typeof required_role === "string" ? { required_role } : {}),
        ...(Object.keys(extensions).length > 0 ? { extensions } : {}),
      };
    }
  }
  return transportProblem(response.status, headerId, response.statusText);
}

export function transportProblem(status: number, requestId: string, title?: string): Problem {
  return {
    type: TRANSPORT_PROBLEM,
    title: title !== undefined && title.length > 0 ? title : status === 0 ? "The request did not reach the server" : `Request failed with status ${status}`,
    status,
    request_id: requestId,
  };
}
